import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FCM } from '@capacitor-community/fcm';
import { Capacitor } from '@capacitor/core';
import { ActionPerformed, PushNotificationSchema, PushNotifications } from '@capacitor/push-notifications';
import { EMPTY, Subject, empty } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CrtNotificationHandler } from '../../../crt/providers/crt-notification-handler';
import { IotNotificationHandler } from '../../../iot/providers/iot-notification-handler.service';
import { UserService } from '../auth/user.service';
import { Events } from '../events/events.provider';
import { LoggerFactory } from '../logger/logger-factory.service';
import { LoggerWrapper } from '../logger/logger-wrapper.service';
import { MultiTechNotificationHandlerProvider } from './multi-tech-notification-handler';
import { Channel } from '@capacitor/push-notifications/dist/esm/definitions';

/**
 * Type de notification
 */
export enum CommonNotificationEmetteurType {
  multitech = 'multitech',
  crt = 'crt',
  iot = 'iot',
}

/*
  Interception des notifications
  et redirection vers CRT ou Multitech
*/
@Injectable({ providedIn: 'root' })
export class CommonNotificationHandler {
  /**
   * Token Firebase
   */
  private _token: string;
  /**
   * Notification event
   * @type {Subject<any>}
   */
  private onNotificationSub = new Subject();

  /**
   * Logger
   */
  private logger: LoggerWrapper;

  /**
   * Constructeur
   * @param firebase
   * @param platform
   * @param http
   * @param userService
   */
  constructor(
    private http: HttpClient,
    private userService: UserService,
    private crtNotificationHandler: CrtNotificationHandler,
    private multiTechNotificationHandler: MultiTechNotificationHandlerProvider,
    private loggerFactory: LoggerFactory,
    private events: Events,
    private iotNotificationHandler: IotNotificationHandler,
    private user: UserService
  ) {
    this.logger = this.loggerFactory.getInstance(this);
  }

  /**
   * Récuperation du token firebase
   * @returns {Promise<string>}
   */
  public async getToken(): Promise<string> {
    if (!Capacitor.isNativePlatform()) {
      return null;
    }

    const OLD_CHANNEL_ID = 'intervention_channel_id';
    const CURRENT_INTERVENTION_CHANNEL_ID = 'intervention_channel_id_v2';
    const DEFAULT_CHANNEL_ID = 'default_channel_id';

    try {
      const { channels } = await PushNotifications.listChannels();
      const oldChannelExists = channels.some((channel) => channel.id === OLD_CHANNEL_ID);

      if (oldChannelExists) {
        await PushNotifications.deleteChannel({ id: OLD_CHANNEL_ID });
        this.logger.info(`Ancien channel supprimé avec succès : ${OLD_CHANNEL_ID}`);
      }
    } catch (error) {
      this.logger.error(error, "Erreur native lors de la gestion de l'ancien channel");
    }

    await PushNotifications.createChannel(<Channel>{
      id: DEFAULT_CHANNEL_ID,
      name: DEFAULT_CHANNEL_ID,
      description: 'Channel de notification par défaut',
      importance: 5,
      sound: null,
      visibility: 1,
      lights: true,
      vibration: true,
    });

    await PushNotifications.createChannel(<Channel>{
      id: CURRENT_INTERVENTION_CHANNEL_ID,
      name: CURRENT_INTERVENTION_CHANNEL_ID,
      description: 'Channel de notification pour les demandes interventions',
      importance: 5,
      sound: 'intervention.mp3',
      visibility: 1,
      lights: true,
      vibration: true,
    });

    let permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive === 'prompt' || permStatus.receive === 'prompt-with-rationale') {
      permStatus = await PushNotifications.requestPermissions();
    }
    await PushNotifications.register();
    if (permStatus.receive !== 'granted') {
      alert('Pour un fonctionnement optimal de D@ctech, vous devez activer les notifications sur le téléphone');
      throw new Error('User denied permissions!');
    }

    PushNotifications.addListener('registration', (token) => {
      console.log('Firebase Plugin getToken', token.value);
      this.saveToken(token.value);
    });

    PushNotifications.addListener('registrationError', (error: any) => {
      console.log('Error: ' + JSON.stringify(error));
    });

    PushNotifications.addListener('pushNotificationReceived', async (notification: PushNotificationSchema) => {
      console.log('notification', notification);
      const data = notification?.data;
      this.onNotificationSub.next({ ...data, body: notification.body, title: notification.title });
    });

    PushNotifications.addListener('pushNotificationActionPerformed', async (notification: ActionPerformed) => {
      console.log('notification', notification);
      const data = notification.notification.data;
      this.onNotificationSub.next({ ...data, tap: true });
    });

    FCM.getToken()
      .then((data) => {
        console.log('FirebasePlugin getToken', data);
        this.saveToken(data.token);
      })
      .catch((err) => console.log(err));

    FCM.refreshToken()
      .then((data) => {
        console.log('onTokenRefresh', data.token);
        this.saveToken(data.token);
      })
      .catch((err) => console.log(err));

    FCM.unsubscribeFrom({ topic: 'mnt' })
      .then(() => {
        if (this.user.isMntProfil() || this.user.isDiffusProfil()) {
          FCM.subscribeTo({ topic: 'mnt' })
            .then((data) => {
              console.log('subscribed to topic mnt');
            })
            .catch((err) => {
              console.warn('Firebase Plugin subscribe mnt err', err);
            });
        }
      })
      .catch((err) => {
        console.warn('Firebase Plugin unsubscribe mnt err', err);
      });

    FCM.unsubscribeFrom({ topic: 'crt' })
      .then(() => {
        if (this.user.isProfilLecture() || this.user.isProfilLectureEcriture()) {
          FCM.subscribeTo({ topic: 'crt' })
            .then((data) => {
              console.log('subscribed to topic crt');
            })
            .catch((err) => {
              console.warn('Firebase Plugin subscribe crt err', err);
            });
        }
      })
      .catch((err) => {
        console.warn('Firebase Plugin unsubscribe crt err', err);
      });

    FCM.unsubscribeFrom({ topic: 'iot' })
      .then(() => {
        if (this.user.isProfilIoT()) {
          FCM.subscribeTo({ topic: 'iot' })
            .then((data) => {
              console.log('subscribed to topic iot');
            })
            .catch((err) => {
              console.warn('Firebase Plugin subscribe iot err', err);
            });
        }
      })
      .catch((err) => {
        console.warn('FirebasePlugin.unsubscribe iot err', err);
      });

    FCM.unsubscribeFrom({ topic: 'crt_lecture' })
      .then(() => {
        if (this.user.isProfilLecture() && !this.user.isProfilLectureEcriture()) {
          FCM.subscribeTo({ topic: 'crt_lecture' })
            .then((data) => {
              console.log('subscribed to topic crt_lecture');
            })
            .catch((err) => {
              console.warn('Firebase Plugin subscribe crt_lecture_ecriture err', err);
            });
        }
      })
      .catch((err) => {
        console.warn('Firebase Plugin unsubscribe crt_lecture_ecriture err', err);
      });

    FCM.unsubscribeFrom({ topic: 'crt_lecture_ecriture' })
      .then(() => {
        if (this.user.isProfilLectureEcriture()) {
          FCM.subscribeTo({ topic: 'crt_lecture_ecriture' })
            .then((data) => {
              console.log('subscribed to topic crt_lecture_ecriture');
            })
            .catch((err) => {
              console.warn('Firebase Plugin subscribe crt_lecture_ecriture err', err);
            });
        }
      })
      .catch((err) => {
        console.warn('Firebase Plugin unsubscribe crt_lecture_ecriture err', err);
      });

    return null;
  }

  /**
   * Supprime le token au logout
   */
  public deleteToken() {
    this.saveToken(null);
  }

  /**
   * Récupère les notifications et les transmets au bon service
   */
  public handleCommonNotifications() {
    return this.onNotificationSub.pipe(
      mergeMap((notification: any) => {
        let observable;
        if (notification && notification.typeEmetteur === CommonNotificationEmetteurType.multitech) {
          observable = this.multiTechNotificationHandler.handleNotification(notification);
          this.events.publish('agenda:forceReload');
        } else if (notification && notification.typeEmetteur === CommonNotificationEmetteurType.iot) {
          observable = this.iotNotificationHandler.handleNotification(notification);
        } else if (notification.typeEmetteur === CommonNotificationEmetteurType.crt || !notification.typeEmetteur) {
          // CRT compatibilité avec les anciennes notifications de CRT
          observable = this.crtNotificationHandler.handleNotifications(notification);
        } else {
          observable = EMPTY;
        }
        return observable.pipe(
          catchError((error) => {
            // Les subscribe des observable sont arrêtés en cas d'erreur
            // On ne veut pas qu'il s'arrête
            this.logger.warn('Catch error notification', error);
            return empty();
          })
        );
      })
    );
  }

  /**
   * Enregistre le token et son association avec l'utilisateur.
   * @param token
   */
  private saveToken(token: string): void {
    if (token === this._token) {
      return;
    }
    console.log('token to save : ' + token);
    const user = this.userService.getCurrentUser();
    console.log(user);
    if (user) {
      this.http
        .put(
          `${environment.apis.API_NOTIFICATION}/tokens/${token}`,
          {
            userId: user.matricule,
            dkCodeRessource: user.dkCodeRessource,
          },
          { responseType: 'text' }
        )
        .subscribe(
          () => (this._token = token),
          (error) => {
            this.logger.error(error, `An error occurred while saving token`);
          }
        );
    }
  }
}
